import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Divider, Pane, Text } from '@fullstory/components';
import { NamedElementRecord } from '@fullstory/proto/fs/services/search-settings';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { APIPropertyLabels, useAiSuggestionsExploreEnabled } from '@fullstory/library-ui';
import {
  AppDispatch,
  fullstorySelectors,
  getVisibleElementProps,
  getElementPropsLoading,
  SearchMetaAsyncRequestActions,
} from '@fullstory/redux';
import { isPendingSuggestion } from '../../utils';
import { ObjectDetailsContent } from '@fullstory/dqm-ui/components/ObjectDetailsContent';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import { useNamedElementActivityData } from '../../hooks/useNamedElementActivityData';

const { getOrgId } = fullstorySelectors;

export const NamedElementBody: React.FC<{
  namedElementRecord: NamedElementRecord;
  omitDescription?: boolean;
}> = ({ namedElementRecord, omitDescription = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const elementPropsLoading = useSelector(getElementPropsLoading);
  const elementProps = useSelector(getVisibleElementProps);

  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);
  const showSuggestedElementContent =
    useAiSuggestionsExploreEnabled() && isPendingSuggestion(namedElementRecord.namedElement);

  const loadConnections = useCallback(() => {
    if (namedElementRecord?.namedElement?.id) {
      dispatch(
        SearchMetaAsyncRequestActions.getCustomSchemaConnections(orgId, {
          elements: [namedElementRecord.namedElement.id],
        }),
      );
    }
  }, [orgId, dispatch, namedElementRecord?.namedElement?.id]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const { properties, loading, failed } = useMemo(() => {
    if (!namedElementRecord?.namedElement?.id) {
      return {
        properties: [],
        loading: false,
        failed: false,
      };
    }
    const properties = elementProps.get(namedElementRecord.namedElement.id);
    const loading = elementPropsLoading.has(namedElementRecord.namedElement.id);
    const failed = !properties && !loading;
    return {
      properties: properties ?? [],
      loading,
      failed,
    };
  }, [namedElementRecord?.namedElement?.id, elementPropsLoading, elementProps]);

  const { activityMonitoringData } = useNamedElementActivityData(
    // N.B. This is a little weird, but we're explicitly passing undefined for named element id because
    // we don't retrieve element details and their metric trends for saved elements in search. If we
    // ever decide to do that and match behavior for saved elements in session replay, we'll want to
    // pass the named element id here.
    undefined,
    namedElementRecord,
  );

  if (showSuggestedElementContent) {
    return (
      <ObjectDetailsContent
        description={namedElementRecord.namedElement?.description || ''}
        showDataDirect={showDataDirect}
        eventSupport={DQMRow.EventSupport.EVENT_SUPPORT_NOT_APPLICABLE}
        activityMonitoring={activityMonitoringData}
      />
    );
  }

  const presentProperties = loading || failed || !!properties.length;

  const descriptionView = (
    <Box paddingBottom={8}>
      <Box paddingBottom={8}>
        <Text appearance="bodySmall" color="secondary">
          {namedElementRecord.namedElement!.description}
        </Text>
      </Box>
      <Divider />
    </Box>
  );

  return (
    <div data-component="NamedElementBody">
      {namedElementRecord?.namedElement?.description && !omitDescription && descriptionView}
      <Box paddingBottom={presentProperties ? 16 : 0}>
        <Box paddingBottom={4}>
          <Text appearance="bodySmall">
            <FormattedMessage
              defaultMessage="{selectorCount, plural, one {Element} other {Elements}}"
              id="NsGuvg"
              values={{
                selectorCount: namedElementRecord?.namedElement?.platformSelectors.length ?? 0,
              }}
            />
          </Text>
        </Box>
        {namedElementRecord.namedElement?.platformSelectors.map((ps, i) => (
          <React.Fragment key={`selector_${i}`}>
            {i > 0 ? (
              <Box margin={[8, 0]}>
                <Text appearance="bodySmall" color="secondary">
                  <FormattedMessage defaultMessage="or" id="Ntjkqd" />
                </Text>
              </Box>
            ) : null}
            <Pane backgroundColor="secondary" borderRadius={4}>
              <Box padding={[4, 8]} marginBottom={4}>
                <Text appearance="mono3" style={{ wordBreak: 'break-all' }} tag="div">
                  {ps.selector}
                </Text>
              </Box>
            </Pane>
          </React.Fragment>
        ))}
      </Box>
      {presentProperties && (
        <>
          <Box paddingBottom={4}>
            <Text appearance="bodySmall">
              <FormattedMessage defaultMessage="Properties" id="aI80kg" />
            </Text>
          </Box>
          <APIPropertyLabels
            properties={properties}
            loading={loading}
            failed={failed}
            onFailedRetry={loadConnections}
          />
        </>
      )}
    </div>
  );
};
